import React, { useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { joiResolver } from '@hookform/resolvers/joi';
import {
  Button,
  CustomSelect,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextFieldForm,
  Typography,
} from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { Controller, useForm } from 'react-hook-form';
import { IExamCandidateCreateEditDetialsData } from '../../models';
import schema from '../../modules/admin/schema/iExamCandidateCreateEditDetails';
import { candidateService } from '../../api';
import {
  CandidateInfoUpdateRequest,
  CreateCandidateFunctionRequest,
} from '../../api/candidate/iexam-create/@opadmin/models';
import { stringToLongDateAndShortTime } from '../../utils/DateUtils';

const useStyles = makeStyles()((theme) =>
({
  updatedByText: {
    color: theme.color.text.textTertiary,
    marginBottom: theme.spacing(2),
  },
  authentication: {
    marginBottom: theme.spacing(3),
  },
  email: {
    marginBottom: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface EditDetailsModalProps {
  modalState: boolean;

  onDialogClose?: (doRefetch?: boolean) => void;

  details: { authentication: string; email: string };

  uuid: string;

  candidateId: string;
}

const EditDetailsModal: React.FC<EditDetailsModalProps> = (props: EditDetailsModalProps) => {
  const {
    modalState = false,
    onDialogClose = (doRefetch = false) => null,
    details: { authentication, email },
    uuid,
    candidateId,
  } = props;

  const updateAuthenticationTypeMutation = candidateService.iexamCreate.opadmin.usePutUpdateAuthenticationType(
    (res: AxiosResponse<undefined>) => {
      onDialogClose(true);
    },
    useCallback(() => {
      // do nothing
    }, [])
  );

  const { mutate: updateAuthenticationTypeMutate } = updateAuthenticationTypeMutation;

  const [authType, setAuthType] = useState<any[]>();
  const [buttonDisable, setButtonDisable] = useState<boolean>(true);

  const { data } = candidateService.iexamCreate.opadmin.useGetCandidateInfoToBeUpdated({
    uuid,
  } as CreateCandidateFunctionRequest);
const form = useForm<IExamCandidateCreateEditDetialsData>({
  mode: 'onSubmit',
  resolver: joiResolver(schema),
  defaultValues: {
    authentication,
    email,
  },
});
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
  } = form;

  useEffect(() => {
    if (data) {
      const codes = data.authType.map((code, index) => ({
        key: index,
        value: code.code,
        children: code.description,
      }));

      setAuthType(codes);
    }
  }, [data]);

  const { classes } = useStyles();
  const { t } = useTranslation();

  const hasChange = () =>
    getValues('authentication') === authentication && getValues('email') === email;

  return (
    <>
      {data ? (
        <Dialog
          id="opadmin-edit-details-dialog"
          fullWidth
          open={modalState}
          onClose={() => onDialogClose()}
          maxWidth="sm"
        >
          <form
            onSubmit={handleSubmit((formData: IExamCandidateCreateEditDetialsData) => {
              const newAuthenticationType = {
                uuid,
                authenticationType: formData.authentication,
                email: formData.email,
                candidateId,
              };

              updateAuthenticationTypeMutate(
                newAuthenticationType as CreateCandidateFunctionRequest & CandidateInfoUpdateRequest
              );
            })}
          >
            <DialogTitle>
              {t('components->>IExamCandidateCreateEditDetails.Edit Details')}
            </DialogTitle>
            <DialogContent>
              <Typography variant="subtitle2" className={classes.updatedByText}>
                {`Last updated by ${data.lastUpdatedBy} on ${stringToLongDateAndShortTime(
                  data.lastUpdatedAt,
                  true
                )}`}
              </Typography>
              <Controller
                control={control}
                name="authentication"
                render={() => (
                  <CustomSelect
                    className={classes.authentication}
                    // eslint-disable-next-line react/destructuring-assignment
                    defaultValue={authentication}
                    displayEmpty
                    menuItems={authType as any[]}
                    onChange={(event) => {
                      const { value } = event.target;
                      setValue('authentication', value as string);
                      setButtonDisable(hasChange);
                    }}
                    label={t('components->>IExamCandidateCreateEditDetails.Authentication')}
                    labelId="inputSelectLabel-candidateResultsExamLevel"
                    id="inputSelect-candidateResultsExamLevel"
                  />
                )}
              />
              <TextFieldForm
                key="IExamCandidateCreateEditDetails.Email"
                form={form}
                label={t('components->>IExamCandidateCreateEditDetails.Email')}
                name="email"
                type="text"
                defaultValue={email}
                error={!!errors.email}
                helperText={errors?.email?.message?.toString()}
                className={classes.email}
                fullWidth
                onChange={() => setButtonDisable(hasChange)}
              />
            </DialogContent>
            <DialogActions>
              <div className={classes.button}>
                <Button type="button" variant="text" onClick={() => onDialogClose()}>
                  Cancel
                </Button>
                <Button type="submit" disabled={buttonDisable}>
                  Confirm
                </Button>
              </div>
            </DialogActions>
          </form>
        </Dialog>
      ) : (
        <></>
      )}
    </>
  );
};
export default EditDetailsModal;
